export type SchoolSession = 'morning' | 'afternoon';

// These values are visual placeholders taken from the reviewed timetable frame. Keep the marker
// true until LTV supplies and signs off its actual bell schedule (D0-08).
export const PERIOD_TIMES_ARE_PLACEHOLDER = true;

export const PERIOD_TIMES: Record<number, string> = {
  1: '07:00',
  2: '07:50',
  3: '08:45',
  4: '09:40',
  5: '10:35',
  6: '13:00',
  7: '13:50',
  8: '14:45',
  9: '15:40',
  10: '16:30',
};

export const SESSION_OF: Record<number, SchoolSession> = {
  1: 'morning',
  2: 'morning',
  3: 'morning',
  4: 'morning',
  5: 'morning',
  6: 'afternoon',
  7: 'afternoon',
  8: 'afternoon',
  9: 'afternoon',
  10: 'afternoon',
};

// 5 morning + 5 afternoon. The single source of truth for how many rows the week grid draws.
export const PERIOD_COUNT = Object.keys(PERIOD_TIMES).length;

export const PERIOD_DURATION_MINUTES = 45;

export function periodStartMinutes(index: number): number | null {
  const value = PERIOD_TIMES[index];
  if (!value) return null;
  const parts = value.split(':');
  return Number(parts[0]) * 60 + Number(parts[1]);
}

function clockLabel(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

// "07:00 – 07:45"; pass lastIndex to span a grouped multi-period row.
export function periodRange(index: number, lastIndex: number = index): string | null {
  const start = periodStartMinutes(index);
  const end = periodStartMinutes(lastIndex);
  if (start === null || end === null) return null;
  return `${clockLabel(start)} – ${clockLabel(end + PERIOD_DURATION_MINUTES)}`;
}

/** The period whose 45-minute window contains these minutes, or null between bells. */
export function periodIndexAt(minutes: number): number | null {
  const indexes = Object.keys(PERIOD_TIMES);
  for (let i = 0; i < indexes.length; i += 1) {
    const index = Number(indexes[i]);
    const start = periodStartMinutes(index);
    if (start === null) continue;
    if (minutes >= start && minutes < start + PERIOD_DURATION_MINUTES) return index;
  }
  return null;
}

// Demo clamp: a rehearsal at 22:00 must still show a live period rather than a dead schedule.
// Inside a period the clock is returned untouched; outside every bell it snaps to the nearest start.
export function clampScheduleMinutes(minutes: number): number {
  if (periodIndexAt(minutes) !== null) return minutes;
  const indexes = Object.keys(PERIOD_TIMES);
  let nearest = minutes;
  let best = Infinity;
  for (let i = 0; i < indexes.length; i += 1) {
    const start = periodStartMinutes(Number(indexes[i]));
    if (start === null) continue;
    const distance = Math.abs(minutes - start);
    if (distance < best) {
      best = distance;
      nearest = start;
    }
  }
  return nearest;
}
